import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuthStore } from '../store/auth';
import { getTalentPoolLabelLegacy } from '../utils/talentPoolDisplay';
import {
  buildTalentPoolColorMaps,
  getTalentPoolTagHex,
  resolveTalentPoolColor,
} from '../utils/talentPoolColors';

export interface TalentPoolDictRow {
  id: number;
  code: string;
  displayName: string;
  enabled: boolean;
  /** false = 不盘：不当被评价人；缺省按 true */
  asEvaluatee?: boolean;
  isSystem: boolean;
  sortOrder: number;
  /** 评价关系分组（同组同色；如 医生 / 助理 / 客服） */
  relationGroup?: string | null;
}

interface TalentPoolLabelsContextValue {
  pools: TalentPoolDictRow[];
  enabledCodes: string[];
  /** 启用且作为被评价人的池（评价关系 Tab / 素质模板库） */
  evaluateeDisplayOrder: string[];
  getTalentPoolLabel: (code: string | null | undefined) => string;
  getTalentPoolColor: (code: string | null | undefined) => string;
  getRelationGroupColor: (group: string | null | undefined) => string;
  getTalentPoolColorHex: (code: string | null | undefined) => string;
  displayOrder: string[];
}

const TalentPoolLabelsContext = createContext<TalentPoolLabelsContextValue | null>(null);

export function TalentPoolLabelsProvider({ children }: { children: ReactNode }) {
  const profile = useAuthStore((s) => s.profile);
  const isLoggedIn = !!profile;

  const { data: pools = [] } = useQuery<TalentPoolDictRow[]>({
    queryKey: ['talent-pools'],
    queryFn: () => api.get('/admin/talent-pools') as Promise<TalentPoolDictRow[]>,
    staleTime: 60_000,
    enabled: isLoggedIn,
  });

  const labelByCode = useMemo(
    () => Object.fromEntries(pools.map((r) => [r.code, r.displayName])),
    [pools],
  );

  const enabledCodes = useMemo(
    () => pools.filter((r) => r.enabled).map((r) => r.code),
    [pools],
  );

  const displayOrder = useMemo(() => {
    return [...pools]
      .filter((p) => p.enabled)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
      .map((p) => p.code);
  }, [pools]);

  const evaluateeDisplayOrder = useMemo(() => {
    return [...pools]
      .filter((p) => p.enabled && p.asEvaluatee !== false)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
      .map((p) => p.code);
  }, [pools]);

  const getTalentPoolLabel = useCallback(
    (code: string | null | undefined) => {
      if (!code) return '-';
      if (labelByCode[code]) return labelByCode[code];
      return getTalentPoolLabelLegacy(code);
    },
    [labelByCode],
  );

  const { colorByCode, colorByRelationGroup } = useMemo(
    () => buildTalentPoolColorMaps(pools),
    [pools],
  );

  const getTalentPoolColor = useCallback(
    (code: string | null | undefined) => {
      if (!code) return 'default';
      return resolveTalentPoolColor(code, colorByCode);
    },
    [colorByCode],
  );

  const getRelationGroupColor = useCallback(
    (group: string | null | undefined) => {
      const key = group?.trim();
      if (!key) return 'default';
      return colorByRelationGroup[key] ?? 'default';
    },
    [colorByRelationGroup],
  );

  const getTalentPoolColorHex = useCallback(
    (code: string | null | undefined) => getTalentPoolTagHex(getTalentPoolColor(code)),
    [getTalentPoolColor],
  );

  const value = useMemo(
    () => ({
      pools,
      enabledCodes,
      evaluateeDisplayOrder,
      getTalentPoolLabel,
      getTalentPoolColor,
      getRelationGroupColor,
      getTalentPoolColorHex,
      displayOrder,
    }),
    [
      pools,
      enabledCodes,
      evaluateeDisplayOrder,
      getTalentPoolLabel,
      getTalentPoolColor,
      getRelationGroupColor,
      getTalentPoolColorHex,
      displayOrder,
    ],
  );

  return (
    <TalentPoolLabelsContext.Provider value={value}>{children}</TalentPoolLabelsContext.Provider>
  );
}

export function useTalentPoolLabels(): TalentPoolLabelsContextValue {
  const ctx = useContext(TalentPoolLabelsContext);
  const fallbackGet = useCallback(
    (code: string | null | undefined) => getTalentPoolLabelLegacy(code),
    [],
  );
  const fallbackColor = useCallback(() => 'default', []);
  const fallbackHex = useCallback(() => getTalentPoolTagHex('default'), []);
  return (
    ctx ?? {
      pools: [],
      enabledCodes: [],
      evaluateeDisplayOrder: [],
      getTalentPoolLabel: fallbackGet,
      getTalentPoolColor: fallbackColor,
      getRelationGroupColor: fallbackColor,
      getTalentPoolColorHex: fallbackHex,
      displayOrder: [],
    }
  );
}
